import * as coreEntities from 'core/store/index';
import { isStudent } from 'core/utils/user';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CHATBOT_MAX_WIDTH_PERCENTAGE, CHATBOT_MIN_WIDTH, detectChatbotMode, useChatbotContext } from '../../../hooks/ChatbotContext';
import { createChatService } from '../../../services/chatService';
import ChatTriggerButton from '../../atoms/ChatTriggerButton/ChatTriggerButton';
import ChatbotResizeHandle from '../../atoms/ChatbotResizeHandle/ChatbotResizeHandle';
import Chatbot from '../Chatbot/Chatbot';

/**
 * Core chatbot widget component that uses the Context and manages UI state
 */
function ChatbotWidgetCore({
  chatService,
  emptyStateTitle,
  emptyStateText,
  historyButtonLabel,
  buttonIconName,
  errorMessage,
  className,
  children,
  getChatContextData,
  animationDuration,
  onOpen,
  onClose,
  isControlled,
  isOpenControlled,
  focusOnOpen,
  practicePageUrls,
  hideFloatingButton,
}) {
  const { t } = useTranslation(['chatbot']);
  const location = useLocation();
  const { isChatbotOpen, openChatbot, closeChatbot, chatbotWidth, setChatbotWidth, mode, setChatbotMode } = useChatbotContext();

  // Get current user from Redux store
  const currentUser = useSelector((state) => coreEntities.auth.selectors.getUser(state));

  // Local UI state
  const [isOpen, setIsOpen] = useState(isOpenControlled || false);
  const [isClosing, setIsClosing] = useState(false);
  const [isButtonHidden, setIsButtonHidden] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // Refs
  const closeTimeoutRef = useRef(null);
  const sidebarRef = useRef(null);
  const inputRef = useRef(null);
  const defaultChatService = useMemo(() => createChatService(), []);

  // Validate user permissions - only show for students with AI enabled
  if (!currentUser?.is_tangerine_ai || !isStudent(currentUser)) {
    return children || null;
  }

  // Handlers
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setIsButtonHidden(false);

    if (!isControlled) {
      closeChatbot();
    }

    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      onClose?.();
    }, animationDuration);
  }, [animationDuration, onClose, closeChatbot, isControlled]);

  const handleOpen = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setIsButtonHidden(true);
    setIsClosing(false);
    setIsOpen(true);

    if (!isControlled) {
      openChatbot();
    }

    onOpen?.();
  }, [onOpen, openChatbot, isControlled]);

  // Calculate max width based on viewport
  const getMaxWidth = useCallback(() => {
    return window.innerWidth * CHATBOT_MAX_WIDTH_PERCENTAGE;
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = chatbotWidth;

      const handleMouseMove = (e) => {
        const deltaX = startX - e.clientX; // Negative because we're dragging from the left edge
        const newWidth = Math.max(CHATBOT_MIN_WIDTH, Math.min(getMaxWidth(), startWidth + deltaX));
        setChatbotWidth(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    [chatbotWidth, setChatbotWidth, getMaxWidth]
  );

  // Effects
  useEffect(() => {
    if (!isControlled && isChatbotOpen !== isOpen) {
      if (isChatbotOpen) {
        handleOpen();
      } else {
        handleClose();
      }
    }
  }, [isChatbotOpen, isOpen, isControlled, handleOpen, handleClose]);

  useEffect(() => {
    if (isControlled && typeof isOpenControlled !== 'undefined') {
      setIsOpen(isOpenControlled);
      setIsClosing(false);
    }
  }, [isControlled, isOpenControlled]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen && !isClosing && focusOnOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen, isClosing, focusOnOpen]);

  // Verify mode matches current URL when chatbot opens
  useEffect(() => {
    if (isOpen && !isClosing) {
      const currentMode = detectChatbotMode(window.location.pathname);
      if (mode !== currentMode) {
        setChatbotMode(currentMode);
      }
    }
  }, [isOpen, isClosing, mode, setChatbotMode]);

  // Update CSS variable when width changes
  useEffect(() => {
    document.documentElement.style.setProperty('--chatbot-width', `${chatbotWidth}px`);
  }, [chatbotWidth]);

  // Handle window resize to enforce max width constraint
  useEffect(() => {
    const handleWindowResize = () => {
      const newMaxWidth = getMaxWidth();
      if (chatbotWidth > newMaxWidth) {
        setChatbotWidth(newMaxWidth);
      }
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [chatbotWidth, setChatbotWidth, getMaxWidth]);

  // Monitor route changes and update mode
  useEffect(() => {
    const detectedMode = detectChatbotMode(location.pathname);

    // Update mode if it changed
    if (mode !== detectedMode) {
      setChatbotMode(detectedMode);

      // Close chatbot on mode change while open
      if (isChatbotOpen) {
        handleClose();
      }
    }
  }, [location.pathname, mode, isChatbotOpen, handleClose, setChatbotMode]);

  // Chat props
  const chatbotProps = useMemo(
    () => ({
      chatService: chatService || defaultChatService,
      emptyStateTitle,
      emptyStateText,
      historyButtonLabel,
      errorMessage,
      onClose: handleClose,
      isVisible: isOpen && !isClosing,
      getChatContextData:
        getChatContextData ||
        (() => ({
          currentPage: window.location.pathname,
          timestamp: new Date().toISOString(),
        })),
      inputRef,
      practicePageUrls,
    }),
    [
      chatService,
      defaultChatService,
      emptyStateTitle,
      emptyStateText,
      historyButtonLabel,
      errorMessage,
      handleClose,
      isOpen,
      isClosing,
      getChatContextData,
      practicePageUrls,
    ]
  );

  return (
    <div
      className={`base-chatbot ${isOpen ? 'base-chatbot--enabled' : ''} ${isClosing ? 'chatbot-closing' : ''} ${isResizing ? 'base-chatbot--resizing' : ''}`}
      role="region"
      aria-label={t('Chat assistant')}
    >
      <div className="base-chatbot__container">{children}</div>

      <div className="base-chatbot__chatbot" ref={sidebarRef} tabIndex={isOpen && !isClosing ? 0 : -1} aria-hidden={!isOpen || isClosing}>
        {isOpen && <ChatbotResizeHandle onResize={handleResizeStart} isResizing={isResizing} />}
        <Chatbot {...chatbotProps} className={className} buttonIconName={buttonIconName} />
      </div>

      {!hideFloatingButton && (
        <div className={`base-chatbot__button-container ${isButtonHidden ? 'base-chatbot__button-container--hidden' : ''}`} aria-hidden={isButtonHidden}>
          <ChatTriggerButton onClick={handleOpen} aria-label={t('Open chat assistant')} aria-expanded={isOpen && !isClosing} />
        </div>
      )}

      {isResizing && <div className="base-chatbot__resize-overlay" />}
    </div>
  );
}

ChatbotWidgetCore.propTypes = {
  chatService: PropTypes.object,
  emptyStateTitle: PropTypes.string,
  emptyStateText: PropTypes.string,
  historyButtonLabel: PropTypes.string,
  buttonIconName: PropTypes.string,
  errorMessage: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  getChatContextData: PropTypes.func,
  animationDuration: PropTypes.number,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  isControlled: PropTypes.bool,
  isOpenControlled: PropTypes.bool,
  focusOnOpen: PropTypes.bool,
  practicePageUrls: PropTypes.array,
  hideFloatingButton: PropTypes.bool,
};

export default ChatbotWidgetCore;
